import * as React from 'react'
import { tv, VariantProps } from 'tailwind-variants'
import { HeadingWithIcon, DropZoneDocuments, FileInfo, CameraButton } from '../atoms'
import { UploadProgress } from '../UploadProgress'
import { useEffect, useState } from 'react'
import ModalDocument from 'components/modalDocument'
import { StepResponseMap } from '../../hooks/useSendDocuments/types'
import { DocumentUploadState, useUploadDisplay } from '../../hooks/useSendDocuments/useDocumentStates'
import { on } from 'events'

const sendPrescriptionVariants = tv({
  base: 'h-full w-full space-y-4 rounded-lg bg-white p-4',
  variants: {
    size: {
      sm: 'space-y-3 p-3',
      default: 'space-y-4 p-4',
      lg: 'space-y-6 p-6'
    }
  },
  defaultVariants: {
    size: 'default'
  }
})

const descriptionVariants = tv({
  base: 'leading-relaxed text-gray-600',
  variants: {
    size: {
      sm: 'text-sm',
      default: 'text-black-600 text-base',
      lg: 'text-lg'
    }
  },
  defaultVariants: {
    size: 'default'
  }
})

interface ExistingDocument {
  id: number
  type?: string
  url: string
  extension: string
  status: string
  emission?: string | null
  merged_docs?: any
  createdAt?: string
  updatedAt?: string
  evaluator?: any
  evaluation_date?: string | null
  filename?: string
  link_pdf?: string
  originalName?: string
  size?: number
}

export interface SendPrescriptionProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onDrop' | 'onDragOver' | 'onDragLeave'>,
    VariantProps<typeof sendPrescriptionVariants> {
  onDrop?: (files: File[]) => void
  onFileSelect?: (files: FileList | null) => void
  onTakePhoto?: () => void
  onDragOver?: (event: React.DragEvent) => void
  onDragLeave?: (event: React.DragEvent) => void
  accept?: string
  multiple?: boolean
  disabled?: boolean
  supportedFiles?: string[]
  maxSize?: string
  title?: string
  description?: string
  icon?: React.ReactNode
  titleColor?: string
  showAsterisk?: boolean
  yellowLabelInformation?: boolean
  descriptionInformation?: string
  anvisaButtons?: boolean
  handleGeneratePowerOfAttorney?: () => void
  handleDownloadStepByStep?: () => void
  loading?: boolean
  stepName?: keyof StepResponseMap
  requestFile?: any
  responseType?: 'normal' | 'ocr'
  onSuccess?: () => void
  documentId?: string
  existingDocuments?: ExistingDocument[]
  existingPrescription?: ExistingDocument[]
  handleContinue?: () => void
  onDeleteDocument?: (documentId: string) => void
  sendDocuments?: (
    stepName: keyof StepResponseMap,
    responseType: 'normal' | 'ocr',
    requestFile: any
  ) => Promise<{ success: boolean; data?: any; error?: string | string[] }>
  uploadState?: DocumentUploadState
  deleteDocument?: (documentId: string) => Promise<{ success: boolean; data?: any; error?: string }>
  documentRefused?: boolean
  setDocumentRefused?: (isOpen: boolean) => void
  rejectedDocumentId?: string | null
  onClearRejectedDocument?: (documentId: string) => void
  hasUploaded?: boolean
  setHasUploaded?: (hasUploaded: boolean) => void
  onKeepDocument?: () => void
}

const SendPrescription = React.forwardRef<HTMLDivElement, SendPrescriptionProps>(
  (
    {
      className,
      size = 'default',
      onDrop,
      onFileSelect,
      onTakePhoto,
      onDragOver,
      onDragLeave,
      accept = '.docx,.png,.jpg,.pdf',
      multiple = false,
      disabled = false,
      supportedFiles = ['.docx', '.png', '.jpg', '.pdf'],
      maxSize = '10MB',
      title = 'Envie sua receita médica',
      description = 'Estamos quase lá! Envie sua receita para que nosso sistema valide o documento e finalize a importação com segurança.',
      icon,
      titleColor = '#6B7B3C',
      showAsterisk = true,
      yellowLabelInformation = false,
      descriptionInformation = '',
      anvisaButtons = false,
      handleGeneratePowerOfAttorney,
      handleDownloadStepByStep,
      loading,
      stepName,
      requestFile,
      responseType,
      onSuccess,
      documentId,
      existingDocuments = [],
      existingPrescription = [],
      onDeleteDocument,
      sendDocuments,
      uploadState,
      deleteDocument,
      documentRefused,
      setDocumentRefused,
      rejectedDocumentId,
      onClearRejectedDocument,
      hasUploaded,
      setHasUploaded,
      onKeepDocument,
      ...props
    },
    ref
  ) => {
    const [currentFile, setCurrentFile] = useState<File | null>(null)
    const [uploadProgress, setUploadProgress] = useState(0)

    const uploadDisplay = useUploadDisplay(
      uploadState || {
        isLoading: false,
        error: null,
        success: false,
        progress: 0
      },
      currentFile
    )

    const handleSendDocument = async () => {
      if (requestFile && stepName && responseType) {
        const result = await sendDocuments?.(
          stepName as keyof StepResponseMap,
          responseType as 'normal' | 'ocr',
          requestFile
        )

        if (result?.success) {
          onSuccess?.()
        }
      }
    }

    useEffect(() => {
      if (requestFile && stepName && responseType) {
        if (requestFile instanceof File) {
          setCurrentFile(requestFile)
          setUploadProgress(0)
        }
        handleSendDocument()
      } else if (!requestFile) {
        setCurrentFile(null)
        setHasUploaded?.(false)
        setUploadProgress(0)
      } else if (requestFile && hasUploaded) {
        setHasUploaded?.(false)
      }
    }, [requestFile, stepName, responseType])

    useEffect(() => {
      if (uploadDisplay.shouldClearFile) {
        const timer = setTimeout(() => {
          setCurrentFile(null)
          setUploadProgress(0)
        }, 1000)

        return () => clearTimeout(timer)
      }
    }, [uploadDisplay.shouldClearFile])

    useEffect(() => {
      if (uploadState?.isLoading) {
        setUploadProgress(0)
        const interval = setInterval(() => {
          setUploadProgress((prev) => {
            if (prev >= 90) {
              clearInterval(interval)
              return 90
            }
            return prev + 10
          })
        }, 200)

        return () => clearInterval(interval)
      }
      if (uploadState?.success) {
        setUploadProgress(100)
      }
    }, [uploadState?.isLoading, uploadState?.success])

    const handleInternalDrop = (files: File[]) => {
      if (files.length > 0) {
        setCurrentFile(files[0])
      }
      onDrop?.(files)
    }

    const handleInternalFileSelect = (files: FileList | null) => {
      if (files && files.length > 0) {
        setCurrentFile(files[0])
      }
      onFileSelect?.(files)
    }

    const handleClearFile = () => {
      setCurrentFile(null)
    }

    const createFileFromDocument = (doc: ExistingDocument): File => {
      const fileName =
        doc.originalName ||
        doc.filename ||
        doc.url.split('/').pop() ||
        `documento_${doc.id}.${doc.extension.split('/')[1] || 'pdf'}`
      const size = doc.size || 0
      const buffer = new ArrayBuffer(size)
      const blob = new Blob([buffer], { type: doc.extension })
      return new File([blob], fileName, { type: doc.extension })
    }

    return (
      <div ref={ref} className={sendPrescriptionVariants({ size, className })} {...props}>
        <div className="w-full space-y-2">
          <HeadingWithIcon icon={icon}>
            <p style={{ color: titleColor }}>
              {title}
              {showAsterisk && <span style={{ color: '#DC4E5A' }}>*</span>}
            </p>
          </HeadingWithIcon>

          <p className={descriptionVariants({ size })}>{description}</p>
        </div>

        {documentRefused && <ModalDocument isOpen={documentRefused} onClose={() => setDocumentRefused?.(false)} />}

        <DropZoneDocuments
          size={size === 'sm' ? 'sm' : size === 'lg' ? 'lg' : 'default'}
          onDrop={handleInternalDrop}
          onFileSelect={handleInternalFileSelect}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          accept={accept}
          multiple={multiple}
          disabled={disabled || uploadState?.isLoading}
        />

        <FileInfo
          size={size === 'sm' ? 'sm' : size === 'lg' ? 'lg' : 'default'}
          supportedFiles={supportedFiles}
          maxSize={maxSize}
        />

        <div className="mt-4 flex justify-start">
          <CameraButton size="default" onTakePhoto={onTakePhoto} disabled={disabled} />
        </div>

        {uploadDisplay.shouldShowProgress && (
          <UploadProgress
            file={currentFile!}
            status={uploadDisplay.displayStatus}
            progress={uploadProgress}
            error={uploadState?.error || undefined}
            documentId={documentId}
            onCancel={
              currentFile
                ? () => {
                    handleClearFile()
                    console.log('documentId', documentId)
                    if (documentId && onDeleteDocument) {
                      onDeleteDocument(documentId)
                    } else if (documentId) {
                      deleteDocument?.(documentId)
                    }
                  }
                : undefined
            }
            size={size === 'sm' ? 'sm' : size === 'lg' ? 'lg' : 'default'}
            onKeepDocument={onKeepDocument}
          />
        )}

        {existingDocuments && existingDocuments.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-lg font-medium text-gray-900">Documentos enviados</h4>
            <div className="space-y-2">
              {existingDocuments.map((doc) => (
                <UploadProgress
                  key={doc.id}
                  file={createFileFromDocument(doc)}
                  status="success"
                  progress={100}
                  onCancel={onDeleteDocument ? () => onDeleteDocument(doc.id.toString()) : undefined}
                  onKeepDocument={onKeepDocument}
                  size={size === 'sm' ? 'sm' : size === 'lg' ? 'lg' : 'default'}
                />
              ))}
            </div>
          </div>
        )}

        {yellowLabelInformation && (
          <div style={{ padding: '0 0 40px 0', width: '100%' }}>
            <div style={{ width: '100%' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  width: '100%',
                  backgroundColor: '#FFEEC299',
                  color: '#92400e',
                  padding: '16px',
                  borderRadius: '2px'
                }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    backgroundColor: 'transparent',
                    border: '1px solid #898483',
                    color: 'gray',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    flexShrink: 0
                  }}>
                  !
                </div>
                <div style={{ flex: 1, color: '#898483', fontSize: '12px' }}>
                  <span style={{ fontWeight: 'bold', textTransform: 'uppercase', color: '#898483' }}>IMPORTANTE: </span>
                  {descriptionInformation}
                </div>
              </div>
            </div>
          </div>
        )}

        {anvisaButtons && (
          <>
            <div>
              <div style={{ width: '100%' }}>
                <div
                  style={{
                    backgroundColor: 'white',
                    padding: '24px'
                  }}>
                  <div className="flex flex-col gap-4 md:flex-row md:items-center md:gap-3">
                    <h4 className="md:w-82 m-0 text-base font-semibold text-gray-700 md:whitespace-nowrap">
                      Quer que a gente faça isso por você?
                    </h4>

                    <p className="m-0 leading-relaxed text-gray-600 md:flex-1">
                      É só clicar em 'Gerar Procuração' — esse documento nos autoriza a emitir por aqui.
                    </p>

                    <button
                      onClick={handleGeneratePowerOfAttorney}
                      disabled={loading}
                      className="md:w-50 w-full cursor-pointer rounded-sm border border-gray-300 bg-white px-6 py-3 text-base font-semibold text-gray-700 transition-all duration-200 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 md:whitespace-nowrap"
                      style={{
                        width: '100%',
                        maxWidth: '200px'
                      }}>
                      {loading ? 'Gerando...' : 'Gerar procuração'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div style={{ width: '100%' }}>
                <div
                  style={{
                    backgroundColor: 'white',
                    padding: '24px'
                  }}>
                  <div className="flex flex-col gap-4 md:flex-row md:items-center md:gap-3">
                    <h4 className="m-0 text-base font-semibold text-gray-700 md:w-52 md:whitespace-nowrap">
                      Prefere emitir sozinho?
                    </h4>

                    <p className="m-0 leading-relaxed text-gray-600 md:flex-1">
                      Clique em 'baixar passo a passo' e leia nosso tutorial de como obter o documento.
                    </p>

                    <button
                      onClick={handleDownloadStepByStep}
                      disabled={loading}
                      className="md:w-50 flex w-full cursor-pointer items-center justify-center rounded-sm border border-gray-300 bg-white px-6 py-3 text-base font-semibold text-gray-700 transition-all duration-200 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 md:whitespace-nowrap"
                      style={{
                        maxWidth: '200px'
                      }}>
                      {loading ? 'Baixando...' : 'Baixar passo a passo'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    )
  }
)

SendPrescription.displayName = 'SendPrescription'

export { SendPrescription, sendPrescriptionVariants }
